import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { deleteCustomersAPI, getCustomersAPI, updateCustomersAPI } from "settings/account/components/Customers/request/request";
import { clearUserProfile } from "./userProfileSlice";

interface FetchCustomerArgs {
	token: string;
	tenantId: number;
  }

interface SelectionState {
  partnerId: number;
  partnerName: string;
  partnerDomain: string;
  tenantId: number;
  tenantName: string;
  tenantDomain: string;
  customerId?: string | null;
  subscriptionId?: string | null;
}

const initialState: SelectionState = {
  partnerId:   0,
  partnerName: "",
  partnerDomain: "", 
  tenantId:    0,
  tenantName:  "",
  tenantDomain: "",
  customerId: null,
  subscriptionId: null,
};

const selectionSlice = createSlice({
  name: "selection",
  initialState,
  reducers: {
    setPartner(
      state,
      action: PayloadAction<{ id: number; name: string, domain: string }>
    ) {
      state.partnerId   = action.payload.id;
      state.partnerName = action.payload.name;
      state.partnerDomain = action.payload.domain;
      // Eagerly land the lens on "Partner Account view" of the new
      // partner instead of resetting tenantId to 0. Two reasons:
      //  1. UX: the Customer dropdown's `customerSelectedValue` uses
      //     `!tenantId` to render "X (Partner Account)". With the old
      //     reset-to-0 the visual said Partner Account but the wire
      //     said "no override" (which for GA triggers backend findAll
      //     per TAGS-API-CONTRACT §3.0). The lens visual and the wire
      //     now agree.
      //  2. Perf: avoids a transient `tenantId === 0` render between
      //     this dispatch and the subsequent `loadCustomers`-driven
      //     `setTenant`. Lens-aware components (Tags Manager etc.)
      //     would otherwise fire a wasteful fetch with no tenantId
      //     (findAll for GA) before the cascade settled.
      // `loadCustomers` may still override this with a specific
      // customer if there's a persisted-tenantId match in the new
      // partner's children — that path still works because the
      // closure captures the previous tenantId.
      state.tenantId   = action.payload.id;
      state.tenantName = action.payload.name;
      state.tenantDomain = action.payload.domain;
      state.customerId = null;
    },
    setTenant(
      state,
      action: PayloadAction<{ id: number; name: string, domain: string, customerId?: string | null; }>
    ) {
      state.tenantId   = action.payload.id;
      state.tenantName = action.payload.name;
      state.tenantDomain = action.payload.domain;
      state.customerId =
      typeof action.payload.customerId === "undefined"
        ? state.customerId ?? null
        : action.payload.customerId;
    },
    setSubscriptionId(
      state,
      action: PayloadAction<string | null>
    ) {
      state.subscriptionId = action.payload;
    },
    clearSelection: () => initialState,
  },
  extraReducers: (builder) => {
    // when we clear the user profile, also clear selection
    builder
    .addCase(clearUserProfile, () => initialState);
  }
});


export const fetchCustomers = createAsyncThunk(
	"account/fetchCustomers",
    async ({ token, tenantId }: FetchCustomerArgs, thunkAPI) => {
      try {
      const response = await getCustomersAPI(token, tenantId);
      return response.data;
      } catch (error) {
      return thunkAPI.rejectWithValue(error);
      }
    }
);
export const updateCustomerName = createAsyncThunk(
  'customers/updateName',
  async ({ token, userId, name }: { token: string; userId: string; name: string }) => {
  try {
    const response = await updateCustomersAPI(token, userId, name)
    return response.data;
  } catch (error) {
    console.error(error)
  }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async ({ token, tenantId, userId }: { token: string; tenantId: number; userId: string }) => {
    try {
      console.log(tenantId, userId)
      const response = await deleteCustomersAPI(token, tenantId, userId)
      return response.data;
    } catch (error){
      console.error(error)
    }
  }
);

// Exportamos la acción y el reducer
export const { setPartner, setTenant, setSubscriptionId, clearSelection } = selectionSlice.actions;
export default selectionSlice.reducer;